import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIDR, formatDate } from "./format";

const NAVY = [10, 17, 40];
const GOLD = [212, 175, 55];
const SLATE = [100, 116, 139];

function drawHeader(doc, company, title, period) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, 210, 32, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 32, 210, 1.2, "F");
  if (company.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", 14, 6, 20, 20); } catch (e) {}
  }
  const x = company.logo_base64 ? 38 : 14;
  const brandW = 196 - x - 30;  // sisakan tempat utk title kanan
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text((company.company_name || "FUJI INDO SURYA ERP SYSTEM").toUpperCase(), x, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  let hy = 17;
  if (company.address) {
    const addrLines = doc.splitTextToSize(company.address, brandW);
    doc.text(addrLines.slice(0, 2), x, hy);
    hy += Math.min(addrLines.length, 2) * 3.2;
  }
  const contactLine = [company.phone && `Telp: ${company.phone}`, company.email].filter(Boolean).join(" · ");
  if (contactLine) { doc.text(contactLine, x, hy); hy += 3.2; }
  if (company.tax_id) { doc.text(`NPWP: ${company.tax_id}`, x, hy); }
  doc.setTextColor(...GOLD);
  doc.setFontSize(11);
  doc.text(title.toUpperCase(), 196, 14, { align: "right" });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(period || "", 196, 20, { align: "right" });
}

function drawFooter(doc, page, total) {
  doc.setFontSize(7);
  doc.setTextColor(...SLATE);
  doc.text(`Halaman ${page} dari ${total}`, 196, 289, { align: "right" });
  doc.text("Dokumen dihasilkan otomatis oleh FUJI INDO SURYA ERP SYSTEM · Bersifat rahasia", 14, 289);
}

export function generateMonthlyReportPDF({ company, period, balanceSheet, incomeStatement, cashFlow, trialBalance, ratios, preview }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pages = [];

  // ---------- Cover ----------
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, 210, 297, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 100, 210, 1.5, "F");
  if (company.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", 90, 40, 30, 30); } catch (e) {}
  }
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("LAPORAN KEUANGAN", 105, 115, { align: "center" });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text((company.company_name || "FUJI INDO SURYA ERP SYSTEM").toUpperCase(), 105, 130, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(200, 200, 220);
  doc.text(period || "Semua Periode", 105, 145, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 200);
  if (company.address) doc.text(company.address.slice(0, 80), 105, 200, { align: "center" });
  const meta = [company.phone, company.email].filter(Boolean).join(" · ");
  if (meta) doc.text(meta, 105, 206, { align: "center" });
  if (company.tax_id) doc.text(`NPWP: ${company.tax_id}`, 105, 212, { align: "center" });
  doc.setTextColor(...GOLD);
  doc.setFontSize(9);
  doc.text(`Dicetak: ${formatDate(new Date().toISOString())}`, 105, 275, { align: "center" });

  // ---------- Balance Sheet ----------
  doc.addPage();
  drawHeader(doc, company, "Neraca (Balance Sheet)", period);
  let y = 42;
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("ASET", 14, y); y += 3;
  autoTable(doc, {
    startY: y,
    head: [["Kode", "Akun", "Saldo"]],
    body: [
      ...balanceSheet.assets.map((a) => [a.code, a.name, formatIDR(a.balance)]),
      [{ content: "TOTAL ASET", colSpan: 2, styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD } }, { content: formatIDR(balanceSheet.total_assets), styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD, halign: "right" } }],
    ],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 20, fontStyle: "bold", textColor: NAVY }, 2: { halign: "right" } },
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9 },
  });
  y = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "bold");
  doc.text("LIABILITAS", 14, y); y += 3;
  autoTable(doc, {
    startY: y,
    head: [["Kode", "Akun", "Saldo"]],
    body: [
      ...balanceSheet.liabilities.map((a) => [a.code, a.name, formatIDR(a.balance)]),
      [{ content: "TOTAL LIABILITAS", colSpan: 2, styles: { fontStyle: "bold", fillColor: [254, 226, 226], textColor: [153, 27, 27] } }, { content: formatIDR(balanceSheet.total_liabilities), styles: { fontStyle: "bold", fillColor: [254, 226, 226], textColor: [153, 27, 27], halign: "right" } }],
    ],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 20, fontStyle: "bold", textColor: NAVY }, 2: { halign: "right" } },
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9 },
  });
  y = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "bold");
  doc.text("EKUITAS", 14, y); y += 3;
  autoTable(doc, {
    startY: y,
    head: [["Kode", "Akun", "Saldo"]],
    body: [
      ...balanceSheet.equity.map((a) => [a.code, a.name, formatIDR(a.balance)]),
      [{ content: "TOTAL EKUITAS", colSpan: 2, styles: { fontStyle: "bold", fillColor: [220, 252, 231], textColor: [21, 128, 61] } }, { content: formatIDR(balanceSheet.total_equity), styles: { fontStyle: "bold", fillColor: [220, 252, 231], textColor: [21, 128, 61], halign: "right" } }],
      [{ content: "LIABILITAS + EKUITAS", colSpan: 2, styles: { fontStyle: "bold", fillColor: GOLD, textColor: NAVY } }, { content: formatIDR(balanceSheet.total_liabilities + balanceSheet.total_equity), styles: { fontStyle: "bold", fillColor: GOLD, textColor: NAVY, halign: "right" } }],
    ],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 20, fontStyle: "bold", textColor: NAVY }, 2: { halign: "right" } },
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9 },
  });

  // ---------- Income Statement ----------
  doc.addPage();
  drawHeader(doc, company, "Laporan Laba Rugi", period);
  const EMERALD = [16, 185, 129];
  const RED = [220, 38, 38];
  const LIGHT_GREEN = [220, 252, 231];
  const LIGHT_RED = [254, 226, 226];
  const LIGHT_SLATE = [241, 245, 249];
  const YELLOW = [254, 243, 199];
  const isRows = [
    [{ content: "PENDAPATAN", colSpan: 2, styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD, halign: "left" } }],
    ...incomeStatement.revenue_lines.map((r) => [
      { content: r.name, styles: { textColor: [30, 41, 59] } },
      { content: formatIDR(r.amount), styles: { halign: "right", textColor: EMERALD, fontStyle: "bold" } },
    ]),
    [
      { content: "Total Pendapatan", styles: { fontStyle: "bold", fillColor: LIGHT_GREEN, textColor: NAVY } },
      { content: formatIDR(incomeStatement.total_revenue), styles: { fontStyle: "bold", halign: "right", fillColor: LIGHT_GREEN, textColor: EMERALD } },
    ],
    [
      { content: "Harga Pokok Penjualan (HPP)", styles: { textColor: [30, 41, 59] } },
      { content: `(${formatIDR(incomeStatement.cogs)})`, styles: { halign: "right", textColor: RED } },
    ],
    [
      { content: "LABA KOTOR", styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD } },
      { content: formatIDR(incomeStatement.gross_profit), styles: { fontStyle: "bold", halign: "right", fillColor: NAVY, textColor: GOLD } },
    ],
    [{ content: "BEBAN OPERASIONAL", colSpan: 2, styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD, halign: "left" } }],
    ...incomeStatement.expense_lines.filter((e) => e.code !== "5000").map((r) => [
      { content: r.name, styles: { textColor: [30, 41, 59] } },
      { content: `(${formatIDR(r.amount)})`, styles: { halign: "right", textColor: RED } },
    ]),
    [
      { content: "Total Beban Operasional", styles: { fontStyle: "bold", fillColor: LIGHT_RED, textColor: NAVY } },
      { content: `(${formatIDR(incomeStatement.opex)})`, styles: { fontStyle: "bold", halign: "right", fillColor: LIGHT_RED, textColor: RED } },
    ],
    [
      { content: "LABA BERSIH", styles: { fontStyle: "bold", fontSize: 12, fillColor: GOLD, textColor: NAVY } },
      { content: formatIDR(incomeStatement.net_income), styles: { fontStyle: "bold", fontSize: 12, halign: "right", fillColor: GOLD, textColor: NAVY } },
    ],
  ];
  autoTable(doc, {
    startY: 44,
    body: isRows,
    styles: { fontSize: 9, cellPadding: 3, lineColor: [226, 232, 240], lineWidth: 0.1 },
    alternateRowStyles: { fillColor: [252, 253, 254] },
    columnStyles: { 1: { halign: "right", cellWidth: 60 } },
    margin: { left: 14, right: 14 },
  });

  // ---------- Cash Flow ----------
  doc.addPage();
  drawHeader(doc, company, "Laporan Arus Kas", period);
  autoTable(doc, {
    startY: 44,
    head: [["Tanggal", "Referensi", "Deskripsi", "Masuk", "Keluar"]],
    body: [
      ...cashFlow.entries.map((e) => [formatDate(e.date), e.ref_number, e.description,
        e.inflow > 0 ? formatIDR(e.inflow) : "", e.outflow > 0 ? formatIDR(e.outflow) : ""]),
      [{ content: "TOTAL ARUS MASUK", colSpan: 3, styles: { fontStyle: "bold", fillColor: [220, 252, 231] } },
       { content: formatIDR(cashFlow.operating_in), styles: { fontStyle: "bold", textColor: [16, 185, 129], fillColor: [220, 252, 231], halign: "right" } },
       { content: "", styles: { fillColor: [220, 252, 231] } }],
      [{ content: "TOTAL ARUS KELUAR", colSpan: 3, styles: { fontStyle: "bold", fillColor: [254, 226, 226] } },
       { content: "", styles: { fillColor: [254, 226, 226] } },
       { content: formatIDR(cashFlow.operating_out), styles: { fontStyle: "bold", textColor: [220, 38, 38], fillColor: [254, 226, 226], halign: "right" } }],
      [{ content: "ARUS KAS BERSIH", colSpan: 3, styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD } },
       { content: formatIDR(cashFlow.net_cash), colSpan: 2, styles: { halign: "right", fontStyle: "bold", fillColor: NAVY, textColor: GOLD } }],
    ],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
    styles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      3: { halign: "right", textColor: [16, 185, 129], fontStyle: "bold" },
      4: { halign: "right", textColor: [220, 38, 38], fontStyle: "bold" },
    },
    margin: { left: 14, right: 14 },
  });

  // ---------- Financial Ratios ----------
  if (ratios) {
    doc.addPage();
    drawHeader(doc, company, "Rasio Keuangan", period);
    const rr = (v, pct) => v == null ? "-" : (pct ? `${(v * 100).toFixed(2)}%` : v.toFixed(2));
    autoTable(doc, {
      startY: 44,
      head: [["Kategori", "Rasio", "Nilai"]],
      body: [
        ["Likuiditas", "Current Ratio", rr(ratios.liquidity.current_ratio, false)],
        ["Likuiditas", "Quick Ratio", rr(ratios.liquidity.quick_ratio, false)],
        ["Likuiditas", "Working Capital", formatIDR(ratios.liquidity.working_capital)],
        ["Profitabilitas", "Gross Margin", rr(ratios.profitability.gross_margin, true)],
        ["Profitabilitas", "Operating Margin", rr(ratios.profitability.operating_margin, true)],
        ["Profitabilitas", "Net Margin", rr(ratios.profitability.net_margin, true)],
        ["Profitabilitas", "ROA", rr(ratios.profitability.roa, true)],
        ["Profitabilitas", "ROE", rr(ratios.profitability.roe, true)],
        ["Leverage", "Debt to Equity", rr(ratios.leverage.debt_to_equity, false)],
        ["Leverage", "Debt to Assets", rr(ratios.leverage.debt_to_assets, false)],
        ["Efisiensi", "Inventory Turnover", rr(ratios.efficiency.inventory_turnover, false)],
        ["Efisiensi", "Receivables Turnover", rr(ratios.efficiency.receivables_turnover, false)],
      ],
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
      styles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: "bold", textColor: NAVY, cellWidth: 40 },
        2: { halign: "right", fontStyle: "bold", textColor: NAVY },
      },
      margin: { left: 14, right: 14 },
    });
  }

  // ---------- Trial Balance ----------
  if (trialBalance) {
    doc.addPage();
    drawHeader(doc, company, "Neraca Percobaan", period);
    autoTable(doc, {
      startY: 44,
      head: [["Kode", "Akun", "Tipe", "Debit", "Kredit"]],
      body: [
        ...trialBalance.rows.map((r) => [r.code, r.name, r.type, r.debit > 0 ? formatIDR(r.debit) : "-", r.credit > 0 ? formatIDR(r.credit) : "-"]),
        [{ content: "TOTAL", colSpan: 3, styles: { fontStyle: "bold", fillColor: NAVY, textColor: GOLD } },
         { content: formatIDR(trialBalance.total_debit), styles: { halign: "right", fontStyle: "bold", fillColor: NAVY, textColor: GOLD } },
         { content: formatIDR(trialBalance.total_credit), styles: { halign: "right", fontStyle: "bold", fillColor: NAVY, textColor: GOLD } }],
      ],
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
      styles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: "bold", textColor: NAVY, cellWidth: 20 },
        3: { halign: "right", textColor: [16, 185, 129] },
        4: { halign: "right", textColor: [220, 38, 38] },
      },
      margin: { left: 14, right: 14 },
    });
  }

  // Footer numbering
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    if (i > 1) drawFooter(doc, i, total);
  }
  const safe = (period || "all").replace(/[^\w-]/g, "_");
  const filename = `Laporan_Keuangan_${safe}.pdf`;
  if (preview) return { url: doc.output("bloburl"), filename };
  doc.save(filename);
}
